import { Controller } from "@hotwired/stimulus"

/**
 * Controls the wizard footer's submit button state based on validation events.
 *
 * This controller listens for window-level custom events dispatched by form controllers
 * (e.g., unified_recipients_controller, message_types_controller) and enables/disables
 * the Next button accordingly.
 *
 * The step value determines which events this controller responds to:
 * - "add_recipients": responds to compose:recipientsChanged events
 * - "choose_message_types": responds to compose:selectionChanged events
 * - other steps: no client-side validation (button state controlled by server)
 */
export default class WizardFooterController extends Controller {
  static targets = ["submitButton"]
  static values = { step: String }

  declare submitButtonTarget: HTMLButtonElement
  declare hasSubmitButtonTarget: boolean
  declare stepValue: string

  validateRecipients(event: CustomEvent<{ hasRecipients: boolean }>) {
    if (this.stepValue !== "add_recipients") return
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.disabled = !event.detail.hasRecipients
  }

  validateMessageTypes(event: CustomEvent<{ hasSelection: boolean }>) {
    if (this.stepValue !== "choose_message_types") return
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.disabled = !event.detail.hasSelection
  }

  validateFileUpload(event: CustomEvent<{ uploading: boolean; hasComplete: boolean; requireUpload: boolean }>) {
    // Only validate on steps that have file uploads
    if (!["compose_sms", "compose_email"].includes(this.stepValue)) return
    if (!this.hasSubmitButtonTarget) return

    const { uploading } = event.detail

    // Disable submit button while any file is uploading
    this.submitButtonTarget.disabled = uploading
  }
}
